import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { ArrowLeft, ArrowRight, Loader2, X, CheckCircle2, Upload, Save } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
    "Identité",
    "Compétences",
    "Outils",
    "Activités",
    "Difficultés",
    "Photos",
];

const NIVEAUX = [
    { value: "1ere", label: "1ère année" },
    { value: "2eme", label: "2ème année" },
];

export default function NewBilan() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editingId = searchParams.get("edit");
    const [step, setStep] = useState(0);
    const [referentiels, setReferentiels] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [loadingExisting, setLoadingExisting] = useState(!!editingId);

    const [form, setForm] = useState({
        nom: "",
        prenom: "",
        niveau: "1ere",
        filiere: "electrotechnique",
        entreprise: "",
        tuteur: "",
        date_debut: "",
        date_fin: "",
        selected_codes: [],
        tools: [],
        toolDraft: "",
        activities: "",
        difficulties: "",
        photos: [],
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/referentiels");
                setReferentiels(data);
            } catch (e) {
                // Non-fatal: form will fall back to an empty referentiel list.
                console.warn("NewBilan: failed to load referentiels", e);
            }
            if (editingId) {
                try {
                    const { data: b } = await api.get(`/bilans/${editingId}`);
                    setForm({
                        nom: b.nom || "",
                        prenom: b.prenom || "",
                        niveau: b.niveau || "1ere",
                        filiere: b.filiere || "electrotechnique",
                        entreprise: b.entreprise || "",
                        tuteur: b.tuteur || "",
                        date_debut: b.date_debut || "",
                        date_fin: b.date_fin || "",
                        selected_codes: (b.selected_competencies || []).map((c) => c.code),
                        tools: b.tools || [],
                        toolDraft: "",
                        activities: b.activities || "",
                        difficulties: b.difficulties || "",
                        photos: (b.photo_paths || [])
                            .filter((p) => typeof p === "string" && p.length > 0)
                            .map((p) => ({ path: p, filename: p.split("/").pop() || "photo" })),
                    });
                } catch (err) {
                    toast.error("Bilan introuvable ou accès refusé.");
                    navigate("/dashboard");
                } finally {
                    setLoadingExisting(false);
                }
                return;
            }
            // Pre-fill from logged-in user if available (only in CREATE mode)
            try {
                const { data: me } = await api.get("/auth/me");
                if (me.nom || me.prenom) {
                    setForm((s) => ({ ...s, nom: me.nom || s.nom, prenom: me.prenom || s.prenom }));
                }
            } catch (e) {
                // Non-fatal: just don't pre-fill — user can type name/prenom manually.
                console.warn("NewBilan: failed to pre-fill user info", e);
            }
        })();
    // eslint-disable-next-line
    }, [editingId]);

    const referentiel = referentiels[form.filiere];

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const toggleCompetency = (code) => {
        setForm((s) => ({
            ...s,
            selected_codes: s.selected_codes.includes(code)
                ? s.selected_codes.filter((c) => c !== code)
                : [...s.selected_codes, code],
        }));
    };

    const addTool = () => {
        const t = form.toolDraft.trim();
        if (!t) return;
        if (!form.tools.includes(t)) update("tools", [...form.tools, t]);
        update("toolDraft", "");
    };
    const removeTool = (t) => update("tools", form.tools.filter((x) => x !== t));

    const handlePhotoUpload = async (files) => {
        if (!files || !files.length) return;
        for (const file of files) {
            if (!file) continue;
            const fd = new FormData();
            fd.append("file", file);
            try {
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                if (data && data.path) {
                    setForm((s) => ({ ...s, photos: [...s.photos, { path: data.path, filename: data.filename || file.name || "photo" }] }));
                }
            } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec d'upload");
            }
        }
    };
    const removePhoto = (path) => update("photos", form.photos.filter((p) => p.path !== path));

    // Accept HEIC/HEIF on the input so iOS Safari shows them in the file picker
    const ACCEPT_IMAGE = "image/*,.heic,.heif";

    const stepValid = useMemo(() => {
        if (step === 0)
            return form.nom && form.prenom && form.entreprise && form.tuteur && form.date_debut && form.date_fin;
        if (step === 1) return form.selected_codes.length > 0;
        return true;
    }, [step, form]);

    const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    const submitBilan = async () => {
        setError("");
        setSubmitting(true);
        try {
            const selected = (referentiel?.competencies || []).filter((c) =>
                form.selected_codes.includes(c.code)
            );
            const payload = {
                nom: form.nom,
                prenom: form.prenom,
                niveau: form.niveau,
                filiere: form.filiere,
                entreprise: form.entreprise,
                tuteur: form.tuteur,
                date_debut: form.date_debut,
                date_fin: form.date_fin,
                selected_competencies: selected,
                tools: form.tools,
                activities: form.activities,
                difficulties: form.difficulties,
                photo_paths: form.photos.map((p) => p.path),
            };
            let bilan;
            if (editingId) {
                const { data } = await api.put(`/bilans/${editingId}`, payload);
                bilan = data;
                toast.success("Bilan mis à jour !");
            } else {
                const { data } = await api.post("/bilans", payload);
                bilan = data;
                toast.success("Bilan enregistré avec succès !");
            }
            navigate(`/bilans/${bilan.id}`);
        } catch (err) {
            const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
            setError(detail);
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
                {/* PROGRESS */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                        <div className="label-eyebrow">Étape {step + 1} / {STEPS.length}</div>
                        <div className="text-xs font-mono text-muted2">{STEPS[step]}</div>
                    </div>
                    <div className="h-1 bg-rule relative" data-testid="progress-bar">
                        <div
                            className="absolute left-0 top-0 h-1 bg-orange transition-all"
                            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                        />
                    </div>
                    <div className="flex justify-between mt-3">
                        {STEPS.map((s, i) => (
                            <button
                                key={s}
                                onClick={() => i < step && setStep(i)}
                                disabled={i > step}
                                className={`text-[10px] font-mono uppercase tracking-wider ${
                                    i === step ? "text-orange font-semibold" : i < step ? "text-ink" : "text-muted2"
                                }`}
                                data-testid={`step-indicator-${i}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="panel p-8 sm:p-10 animate-slide-in-right" key={step}>
                    {step === 0 && (
                        <div className="space-y-6" data-testid="step-identity">
                            <div>
                                <div className="label-eyebrow mb-1">01 — Identité</div>
                                <h2 className="h-display text-3xl font-semibold">Qui êtes-vous ?</h2>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-eyebrow mb-2 block">Prénom</label>
                                    <input className="field-input" value={form.prenom} onChange={(e) => update("prenom", e.target.value)} data-testid="bilan-prenom-input" />
                                </div>
                                <div>
                                    <label className="label-eyebrow mb-2 block">Nom</label>
                                    <input className="field-input" value={form.nom} onChange={(e) => update("nom", e.target.value)} data-testid="bilan-nom-input" />
                                </div>
                                <div>
                                    <label className="label-eyebrow mb-2 block">Filière</label>
                                    <select className="field-input" value={form.filiere} onChange={(e) => { update("filiere", e.target.value); update("selected_codes", []); }} data-testid="bilan-filiere-select">
                                        <option value="electrotechnique">BTS Électrotechnique</option>
                                        <option value="ciel">BTS CIEL</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label-eyebrow mb-2 block">Niveau</label>
                                    <select className="field-input" value={form.niveau} onChange={(e) => update("niveau", e.target.value)} data-testid="bilan-niveau-select">
                                        {NIVEAUX.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="label-eyebrow mb-2 block">Entreprise d'accueil</label>
                                    <input className="field-input" value={form.entreprise} onChange={(e) => update("entreprise", e.target.value)} placeholder="Ex: Schneider Electric, Bouygues Énergies & Services…" data-testid="bilan-entreprise-input" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="label-eyebrow mb-2 block">Tuteur en entreprise</label>
                                    <input className="field-input" value={form.tuteur} onChange={(e) => update("tuteur", e.target.value)} placeholder="Nom et fonction" data-testid="bilan-tuteur-input" />
                                </div>
                                <div>
                                    <label className="label-eyebrow mb-2 block">Date de début</label>
                                    <input type="date" className="field-input" value={form.date_debut} onChange={(e) => update("date_debut", e.target.value)} data-testid="bilan-date-debut-input" />
                                </div>
                                <div>
                                    <label className="label-eyebrow mb-2 block">Date de fin</label>
                                    <input type="date" className="field-input" value={form.date_fin} onChange={(e) => update("date_fin", e.target.value)} data-testid="bilan-date-fin-input" />
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="space-y-6" data-testid="step-competencies">
                            <div>
                                <div className="label-eyebrow mb-1">02 — Compétences évaluées</div>
                                <h2 className="h-display text-3xl font-semibold">Référentiel {referentiel?.label}</h2>
                                <p className="text-sm text-muted2 mt-2">Cochez les compétences mises en œuvre durant les deux semaines.</p>
                            </div>
                            {(() => {
                                const competencies = referentiel?.competencies || [];
                                const hasCategories = competencies.some((c) => c.category);
                                const transversales = competencies.filter((c) => c.category === "transversale");
                                const specifiques = competencies.filter((c) => c.category === "specifique");
                                const renderCard = (c) => {
                                    const checked = form.selected_codes.includes(c.code);
                                    return (
                                        <button
                                            type="button"
                                            key={c.code}
                                            onClick={() => toggleCompetency(c.code)}
                                            className={`text-left border rounded-sm p-3.5 transition-all flex gap-3 items-start ${
                                                checked ? "border-orange bg-orange/5" : "border-rule hover:border-ink/40 bg-white"
                                            }`}
                                            data-testid={`competency-${c.code}`}
                                        >
                                            <span className={`shrink-0 w-5 h-5 mt-0.5 border flex items-center justify-center text-white ${
                                                checked ? "bg-orange border-orange" : "bg-white border-ink/20"
                                            }`}>
                                                {checked && <CheckCircle2 size={14} />}
                                            </span>
                                            <div>
                                                <div className="font-mono text-xs text-orange font-bold">{c.code}</div>
                                                <div className="text-sm text-ink mt-0.5 leading-snug">{c.label}</div>
                                            </div>
                                        </button>
                                    );
                                };
                                if (hasCategories) {
                                    return (
                                        <div className="space-y-6">
                                            {transversales.length > 0 && (
                                                <div>
                                                    <div className="label-eyebrow mb-3 flex items-center gap-2">
                                                        <span className="inline-block w-1.5 h-1.5 bg-orange" />
                                                        Compétences transversales · {transversales.length}
                                                    </div>
                                                    <div className="grid sm:grid-cols-2 gap-2">{transversales.map(renderCard)}</div>
                                                </div>
                                            )}
                                            {specifiques.length > 0 && (
                                                <div>
                                                    <div className="label-eyebrow mb-3 flex items-center gap-2">
                                                        <span className="inline-block w-1.5 h-1.5 bg-ink" />
                                                        Compétences spécifiques métier · {specifiques.length}
                                                    </div>
                                                    <div className="grid sm:grid-cols-2 gap-2">{specifiques.map(renderCard)}</div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return <div className="grid sm:grid-cols-2 gap-2">{competencies.map(renderCard)}</div>;
                            })()}
                            <div className="text-xs font-mono text-muted2">
                                {form.selected_codes.length} compétence(s) cochée(s)
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-6" data-testid="step-tools">
                            <div>
                                <div className="label-eyebrow mb-1">03 — Outils & logiciels</div>
                                <h2 className="h-display text-3xl font-semibold">Quels outils avez-vous utilisés ?</h2>
                                <p className="text-sm text-muted2 mt-2">Ajoutez chaque outil/logiciel un par un (Multimètre, AutoCAD, Schneider Unity, etc.).</p>
                            </div>
                            <div className="flex gap-2">
                                <input
                                    className="field-input flex-1"
                                    placeholder="Ex: Multimètre Fluke 117"
                                    value={form.toolDraft}
                                    onChange={(e) => update("toolDraft", e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTool())}
                                    data-testid="bilan-tool-input"
                                />
                                <button type="button" onClick={addTool} className="btn-secondary" data-testid="bilan-add-tool-button">
                                    Ajouter
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {form.tools.length === 0 && <span className="text-sm text-muted2">Aucun outil ajouté.</span>}
                                {form.tools.map((t) => (
                                    <span key={t} className="pill bg-ink text-white inline-flex items-center" data-testid={`tool-pill-${t}`}>
                                        {t}
                                        <button onClick={() => removeTool(t)} className="ml-1 hover:text-orange">
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6" data-testid="step-activities">
                            <div>
                                <div className="label-eyebrow mb-1">04 — Activités réalisées</div>
                                <h2 className="h-display text-3xl font-semibold">Décrivez ce que vous avez fait.</h2>
                            </div>
                            <textarea
                                rows={10}
                                className="field-input resize-none"
                                placeholder="Exemple : Câblage d'une armoire électrique selon le schéma X, mise en service d'un automate Schneider M340…"
                                value={form.activities}
                                onChange={(e) => update("activities", e.target.value)}
                                data-testid="bilan-activities-textarea"
                            />
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-6" data-testid="step-difficulties">
                            <div>
                                <div className="label-eyebrow mb-1">05 — Difficultés rencontrées</div>
                                <h2 className="h-display text-3xl font-semibold">Quelles difficultés avez-vous eues ?</h2>
                                <p className="text-sm text-muted2 mt-2">Cette partie aide votre formateur à mieux vous accompagner. Soyez précis.</p>
                            </div>
                            <textarea
                                rows={10}
                                className="field-input resize-none"
                                placeholder="Exemple : Difficultés à interpréter le schéma de puissance, manque de pratique sur les variateurs…"
                                value={form.difficulties}
                                onChange={(e) => update("difficulties", e.target.value)}
                                data-testid="bilan-difficulties-textarea"
                            />
                        </div>
                    )}

                    {step === 5 && (
                        <div className="space-y-6" data-testid="step-photos">
                            <div>
                                <div className="label-eyebrow mb-1">06 — Photos d'activités</div>
                                <h2 className="h-display text-3xl font-semibold">Ajoutez vos photos.</h2>
                                <p className="text-sm text-muted2 mt-2">JPG/PNG/WEBP. 8 Mo max par photo. Optionnel.</p>
                            </div>
                            <label htmlFor="photos-input" className="block border-2 border-dashed border-rule rounded-sm p-10 text-center cursor-pointer hover:border-orange hover:bg-orange/5 transition-colors">
                                <Upload size={28} className="text-orange mx-auto mb-3" strokeWidth={1.5} />
                                <div className="font-medium">Cliquez pour ajouter des photos</div>
                                <div className="text-xs text-muted2 mt-1">Vous pouvez en ajouter plusieurs en même temps</div>
                                <input
                                    id="photos-input"
                                    type="file"
                                    multiple
                                    accept={ACCEPT_IMAGE}
                                    className="hidden"
                                    onChange={(e) => handlePhotoUpload(e.target.files)}
                                    data-testid="bilan-photos-input"
                                />
                            </label>
                            {form.photos.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photos-grid">
                                    {form.photos.map((p) => (
                                        <div key={p.path} className="relative group border border-rule">
                                            <img
                                                src={`${window.location.origin}/api/files/${p.path}`}
                                                alt={p.filename}
                                                className="w-full h-32 object-cover"
                                            />
                                            <button
                                                onClick={() => removePhoto(p.path)}
                                                className="absolute top-1 right-1 bg-ink text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                data-testid={`remove-photo-${p.path}`}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Recap shown on the last step */}
                            <div className="panel p-5 bg-surface mt-6">
                                <div className="label-eyebrow mb-3">Récapitulatif</div>
                                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                    <div className="flex justify-between"><dt className="text-muted2">Étudiant</dt><dd className="font-medium">{form.prenom} {form.nom}</dd></div>
                                    <div className="flex justify-between"><dt className="text-muted2">Filière</dt><dd>{referentiel?.label}</dd></div>
                                    <div className="flex justify-between"><dt className="text-muted2">Niveau</dt><dd>{form.niveau === "1ere" ? "1ère année" : "2ème année"}</dd></div>
                                    <div className="flex justify-between"><dt className="text-muted2">Entreprise</dt><dd className="font-medium">{form.entreprise}</dd></div>
                                    <div className="flex justify-between"><dt className="text-muted2">Tuteur</dt><dd>{form.tuteur}</dd></div>
                                    <div className="flex justify-between"><dt className="text-muted2">Période</dt><dd>{form.date_debut} → {form.date_fin}</dd></div>
                                    <div className="flex justify-between"><dt className="text-muted2">Compétences</dt><dd>{form.selected_codes.length} cochées</dd></div>
                                    <div className="flex justify-between"><dt className="text-muted2">Outils</dt><dd>{form.tools.length}</dd></div>
                                    <div className="flex justify-between"><dt className="text-muted2">Photos</dt><dd>{form.photos.length}</dd></div>
                                </dl>
                            </div>

                            {error && (
                                <div className="text-sm text-destructive border border-destructive/20 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="bilan-send-error">
                                    {error}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* NAV */}
                <div className="mt-6 flex items-center justify-between">
                    {step > 0 ? (
                        <button onClick={prev} className="btn-outline" data-testid="bilan-prev-button">
                            <ArrowLeft size={16} /> Précédent
                        </button>
                    ) : <span />}
                    {step < STEPS.length - 1 ? (
                        <button onClick={next} disabled={!stepValid} className="btn-primary" data-testid="bilan-next-button">
                            Suivant <ArrowRight size={16} />
                        </button>
                    ) : (
                        <button
                            onClick={submitBilan}
                            disabled={submitting}
                            className="btn-primary !py-3"
                            data-testid="bilan-submit-button"
                        >
                            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {submitting
                                ? (editingId ? "Mise à jour…" : "Enregistrement…")
                                : (editingId ? "Mettre à jour mon bilan" : "Enregistrer mon bilan")}
                        </button>
                    )}
                </div>
            </main>
        </div>
    );
}
